/**
 * @file 사내 게시판 API
 *
 * 게시판 유형(문서양식·경조사·사내·익명·공지사항 등), 글, 댓글, 첨부를 다룹니다.
 * 게시판 생성·수정·삭제와 공지 고정·글 이동은 전체 관리자만 호출할 수 있고, 서버가 다시 검사합니다.
 */

import { api } from "../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const boardApi = {
    // ── 게시판 유형 ────────────────────────────────────────────────────────
    /** 게시판 목록 조회
     * @param {boolean} [includeInactive=false] 숨김 게시판 포함 (관리자만 유효)
     */
    listTypes:  (includeInactive = false) =>
        api.get(`${API_BASE}/board/types`, includeInactive ? { includeInactive: true } : {}),

    /** 게시판 단건 조회 */
    getType:    (typeId)       => api.get(`${API_BASE}/board/types/${typeId}`),

    /** 게시판 생성 (관리자) */
    createType: (data)         => api.post(`${API_BASE}/board/types`, data),

    /** 게시판 수정 (관리자) */
    updateType: (typeId, data) => api.put(`${API_BASE}/board/types/${typeId}`, data),

    /** 게시판 삭제 (관리자) — 기본 게시판이거나 글이 남아 있으면 거부됩니다. */
    deleteType: (typeId)       => api.delete(`${API_BASE}/board/types/${typeId}`),

    /** 게시판 순서 변경 (관리자) — 배열 순서대로 sortOrder 재부여 */
    reorderTypes: (orderedIds) => api.put(`${API_BASE}/board/types/reorder`, orderedIds),

    // ── 글 ────────────────────────────────────────────────────────────────
    /** 게시판 글 목록 (공지 먼저 · 최신순)
     * @param {number} typeId 게시판 ID
     * @param {object} [params] { keyword, scope: "ALL"|"TITLE"|"AUTHOR", page, size }
     */
    listPosts:  (typeId, params = {}) => api.get(`${API_BASE}/board/types/${typeId}/posts`, params),

    /** 게시판별 최근 글 묶음 — 게시판 홈 카드용(카드 수만큼 왕복하지 않게 한 번에 받는다)
     * @param {number[]} typeIds 게시판 ID 목록
     * @param {number} [limit=10] 게시판당 글 수
     */
    recent:     (typeIds, limit = 10) =>
        api.get(`${API_BASE}/board/recent`, { typeIds: typeIds.join(","), limit }),

    /** 글 상세
     * @param {number} postId
     * @param {boolean} [countView=true] 조회수 증가 여부 — 수정 화면 진입 시엔 false
     */
    getPost:    (postId, countView = true) =>
        api.get(`${API_BASE}/board/posts/${postId}`, { countView }),

    /** 글 작성 — parentId 를 주면 답글 */
    createPost: (typeId, data)  => api.post(`${API_BASE}/board/types/${typeId}/posts`, data),

    /** 글 수정 */
    updatePost: (postId, data)  => api.put(`${API_BASE}/board/posts/${postId}`, data),

    /** 글 삭제 — 답글·댓글·첨부까지 함께 삭제됩니다. */
    deletePost: (postId)        => api.delete(`${API_BASE}/board/posts/${postId}`),

    /** 공지 고정 토글 (관리자) */
    toggleNotice: (postId)      => api.post(`${API_BASE}/board/posts/${postId}/notice`, {}),

    /** 글을 다른 게시판으로 이동 (관리자) */
    movePost:   (postId, boardTypeId) =>
        api.patch(`${API_BASE}/board/posts/${postId}/move`, { boardTypeId }),

    // ── 댓글 ──────────────────────────────────────────────────────────────
    /** 댓글 목록 */
    listComments:  (postId)          => api.get(`${API_BASE}/board/posts/${postId}/comments`),

    /** 댓글 작성 (최대 2000자) */
    createComment: (postId, content) => api.post(`${API_BASE}/board/posts/${postId}/comments`, { content }),

    /** 댓글 수정 */
    updateComment: (commentId, content) => api.put(`${API_BASE}/board/comments/${commentId}`, { content }),

    /** 댓글 삭제 */
    deleteComment: (commentId)       => api.delete(`${API_BASE}/board/comments/${commentId}`),

    // ── 첨부 ──────────────────────────────────────────────────────────────
    /** 첨부 업로드 (multipart)
     * @param {number} postId 글 ID
     * @param {File} file
     * @param {number} [commentId] 댓글 첨부일 때만
     */
    uploadAttachment: (postId, file, commentId) => {
        const form = new FormData();
        form.append("file", file);
        if (commentId) form.append("commentId", commentId);
        return api.upload(`${API_BASE}/board/posts/${postId}/attachments`, form);
    },

    /** 글 첨부 목록 */
    listAttachments:   (postId)       => api.get(`${API_BASE}/board/posts/${postId}/attachments`),

    /** 첨부 삭제 — 올린 본인 또는 관리자 */
    deleteAttachment:  (attachmentId) => api.delete(`${API_BASE}/board/attachments/${attachmentId}`),

    /** 첨부 미리보기 (blob) — PDF·이미지·엑셀·텍스트는 원본, 오피스는 서버 PDF 변환 */
    previewAttachment: (attachmentId) => api.download(`${API_BASE}/board/attachments/${attachmentId}/preview`),
};
